import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import s from "./Inspections.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useInspection, useWorkOrders } from "../../api/queries";
import { isFailure, itemLabel, type Observation } from "../../domain/inspections";
import type { WorkOrder } from "../../domain/workOrders";
import { Button, Card, ErrorState, PriorityBadge, Skeleton, StatusBadge } from "../../design/components";
import { formatDateTime } from "../../lib/format";
import { t, tn } from "../../i18n";
import { ScoreCell, StatusPill } from "./InspectionsPage";

/**
 * One round, reviewed: what was asked, what was answered, what failed, and
 * what those failures produced. Failures come first; the passes are there
 * because "we checked and it was fine" is the record an auditor wants, but
 * they are folded until asked for.
 */
export function InspectionDetailPage() {
  const { id = "" } = useParams();
  const { site } = useSession();
  const insp = useInspection(id);
  const orders = useWorkOrders();
  const [showPasses, setShowPasses] = useState(false);

  /* Exact first: the failed items' defect ids against each order's defect_ids (work_order_defect).
     Older platform rows without defect_ids fall back to a courtesy match on asset tag or zone, and
     the card says which it is showing. */
  const related = useMemo(() => {
    const d = insp.data;
    if (!d) return { items: [] as WorkOrder[], exact: true };
    const failed = d.observations.filter(isFailure);
    const open = (orders.data?.items ?? []).filter((w) => w.status !== "cancelled");
    const defectIds = new Set(failed.map((o) => o.defectId).filter((x): x is string => !!x));
    const joined = open.filter((w) => w.defectIds?.some((id) => defectIds.has(id)));
    if (joined.length || open.some((w) => w.defectIds)) return { items: joined, exact: true };
    const byAsset = open.filter((w) => failed.some((o) => o.assetTag && w.assetTag === o.assetTag));
    const byZone = open.filter(
      (w) => !byAsset.includes(w) && failed.some((o) => o.zoneCode && w.zoneCode === o.zoneCode),
    );
    return { items: [...byAsset, ...byZone], exact: false };
  }, [insp.data, orders.data]);

  if (insp.isPending) {
    return (
      <Page eyebrow={t("{code} · inspection", { code: site.code })} title="…">
        <Skeleton rows={6} height={18} />
      </Page>
    );
  }
  if (insp.isError) {
    return (
      <Page
        eyebrow={t("{code} · inspection", { code: site.code })}
        title={id}
        actions={<Link to="/inspections">{t("← All inspections")}</Link>}
      >
        <ErrorState error={insp.error} retry={() => void insp.refetch()} />
      </Page>
    );
  }
  const d = insp.data;
  const failures = d.observations.filter(isFailure);
  const others = d.observations.filter((o) => !isFailure(o));
  const measured = others.filter((o) => o.result === "value" && o.valueNum != null);

  return (
    <Page
      eyebrow={t("{code} · inspection · {template}", { code: site.code, template: d.template })}
      title={
        <span style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {d.templateName}
          <StatusPill status={d.status} />
          {d.capturedOffline && <span className={s.offline}>{t("OFFLINE")}</span>}
        </span>
      }
      actions={<Link to="/inspections">{t("← All inspections")}</Link>}
    >
      <div className={s.detail}>
        <div style={{ display: "grid", gap: "var(--gap)" }}>
          <Card>
            <div className={s.facts}>
              <Fact label={t("Zone")} value={d.zoneCode} mono />
              <Fact label={t("Scheduled")} value={d.scheduledFor ? formatDateTime(d.scheduledFor) : undefined} />
              <Fact label={t("Started")} value={d.startedAt ? formatDateTime(d.startedAt) : undefined} />
              <Fact label={t("Completed")} value={d.completedAt ? formatDateTime(d.completedAt) : undefined} />
              <div className={s.fact}>
                <span className={s.factLabel}>{t("Score")}</span>
                <span className={s.factValue}>
                  <ScoreCell score={d.score} coverage={d.coveragePct} status={d.status} />
                </span>
              </div>
              <Fact
                label={t("Coverage")}
                value={d.coveragePct != null ? t("{pct}% of the checklist", { pct: d.coveragePct }) : undefined}
              />
              <Fact label={t("Answers")} value={String(d.observations.length)} mono />
              <Fact label={t("Failed")} value={String(failures.length)} mono />
            </div>
          </Card>

          <Card
            title={failures.length ? tn(failures.length, "{n} failed item", "{n} failed items") : "No failed items"}
            sub={
              failures.length
                ? "each failure is a defect the pack raised; the work it produced is on the right"
                : "every item checked was within limits"
            }
          >
            {failures.length > 0 && (
              <div className={s.obs} data-testid="failures">
                {failures.map((o) => (
                  <ObservationRow key={o.id} o={o} />
                ))}
              </div>
            )}
          </Card>

          {measured.length > 0 && (
            <Card title="Measurements" sub="items the pack judges by value">
              <div className={s.obs}>
                {measured.map((o) => (
                  <ObservationRow key={o.id} o={o} />
                ))}
              </div>
            </Card>
          )}

          <Card
            title={tn(others.length - measured.length, "{n} other item", "{n} other items")}
            sub="passed, not applicable, or notes"
            actions={
              <Button size="sm" onClick={() => setShowPasses((v) => !v)}>
                {showPasses ? t("Fold") : t("Show")}
              </Button>
            }
          >
            {showPasses ? (
              <div className={s.obs}>
                {others
                  .filter((o) => !(o.result === "value" && o.valueNum != null))
                  .map((o) => (
                    <ObservationRow key={o.id} o={o} />
                  ))}
              </div>
            ) : (
              <div className={s.collapsed}>{t("Folded — the record is kept; press Show to read every answer.")}</div>
            )}
          </Card>
        </div>

        <div style={{ display: "grid", gap: "var(--gap)" }}>
          <Card
            title="Resulting work"
            sub={
              related.items.length
                ? related.exact
                  ? "work orders closing this round's defects"
                  : "work orders at the failed items' asset or zone (this platform does not list defect ids)"
                : undefined
            }
          >
            {orders.isPending ? (
              <Skeleton rows={3} />
            ) : related.items.length === 0 ? (
              <div className="muted" style={{ fontSize: 13.5 }}>
                {failures.length
                  ? t("No work order is open at these assets or zones yet.")
                  : t("Nothing failed, so nothing was raised.")}
              </div>
            ) : (
              <div className={s.obs}>
                {related.items.map((w) => (
                  <Link key={w.id} to={`/work-orders/${w.id}`} className={s.linkRow}>
                    <PriorityBadge priority={w.priority} />
                    <span>
                      <span className="mono">{w.number}</span> · {w.title}
                    </span>
                    <StatusBadge status={w.status} />
                  </Link>
                ))}
              </div>
            )}
          </Card>
          {d.attrs && Object.keys(d.attrs).length > 0 && (
            <Card title="Attributes">
              <pre className="mono" style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(d.attrs, null, 2)}
              </pre>
            </Card>
          )}
        </div>
      </div>
    </Page>
  );
}

/** The platform's result codes, in the words the screen has always shown. */
const RESULT_LABEL: Record<NonNullable<Observation["result"]>, string> = {
  pass: "pass",
  fail: "fail",
  na: "na",
  value: "value",
};

function ObservationRow({ o }: { o: Observation }) {
  const fail = isFailure(o);
  const cls =
    o.result === "pass"
      ? s.resultPass
      : o.result === "fail"
        ? s.resultFail
        : o.result === "na"
          ? s.resultNa
          : s.resultValue;
  return (
    <div className={`${s.obsRow} ${fail ? s.obsRowFail : ""}`}>
      <span className={`${s.result} ${cls}`}>{o.result ? t(RESULT_LABEL[o.result]) : "—"}</span>
      <div>
        <div className={s.obsItem}>
          {itemLabel(o.itemKey)}
          {o.valueText && o.result !== "pass" && <span className="muted"> — {o.valueText}</span>}
        </div>
        <div className={s.obsMeta}>
          {o.assetTag && <span>{o.assetTag}</span>}
          {o.zoneCode && <span>{o.zoneCode}</span>}
          {o.valueNum != null && <span>{o.valueNum}</span>}
          {o.defectId && (
            <span title={t("Defect raised by the pack's on_fail")}>{t("defect {id}", { id: o.defectId })}</span>
          )}
          {o.point && (
            <span>
              {o.point.lat.toFixed(5)}, {o.point.lon.toFixed(5)}
            </span>
          )}
        </div>
        {o.notes && <div className={s.obsNotes}>{o.notes}</div>}
        {o.skippedReason && <div className={s.obsNotes}>{t("Skipped: {reason}", { reason: o.skippedReason })}</div>}
      </div>
      <div className={s.obsRight}>{o.photos > 0 ? tn(o.photos, "{n} photo", "{n} photos") : ""}</div>
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className={s.fact}>
      <span className={s.factLabel}>{label}</span>
      <span className={`${s.factValue} ${mono ? "mono" : ""}`}>{value ?? <span className="muted">—</span>}</span>
    </div>
  );
}
